import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, LogIn, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Navbar({ logoUrl, content, transparent = true }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { isAuthed, openLogin, logout, user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isLight = !transparent || scrolled;

  const links = [
    { to: "/listings", label: "Listings" },
    { to: "/#how", label: "How it works" },
    { to: "/#cities", label: "Cities" },
    { to: "/#faq", label: "FAQ" },
  ];

  const goToSignIn = () => {
    if (isAuthed) {
      navigate("/listings");
    } else {
      openLogin({ redirectTo: "/listings" });
    }
  };

  const onSignOut = () => {
    logout();
    navigate("/");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        isLight
          ? "backdrop-blur-md bg-[#F7F6F2]/90 border-b border-[#E4E1D9]"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="container-z flex items-center justify-between py-2 sm:py-2.5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo-link">
          <span className="w-2 h-2 rounded-full bg-[#C86646]" />
          <img src={logoUrl} alt="Zumroo" className="h-8 w-8 rounded-lg object-contain" />
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`text-sm font-medium transition-colors ${
                isLight ? "text-[#1A231E] hover:text-[#C86646]" : "text-white/90 hover:text-white"
              }`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {isAuthed ? (
            <>
              <span
                className={`hidden sm:inline text-sm font-medium ${isLight ? "text-[#1A231E]" : "text-white"}`}
                data-testid="navbar-user-name"
              >
                Hi, {(user?.name || "you").split(" ")[0]}
              </span>
              <button
                onClick={onSignOut}
                className={`hidden sm:inline-flex text-sm font-semibold rounded-full px-3 py-1.5 ${
                  isLight ? "text-[#1A231E] hover:bg-[#EAF1ED]" : "text-white hover:bg-white/10"
                }`}
                data-testid="navbar-signout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Sign out
              </button>
              <Link
                to="/listings"
                className="hidden sm:inline-flex btn-primary !py-2 !px-4 text-sm"
                data-testid="navbar-listings-btn"
              >
                Browse
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </>
          ) : (
            <>
              <button
                onClick={goToSignIn}
                className={`hidden sm:inline-flex text-sm font-semibold transition rounded-full px-3 py-1.5 ${
                  isLight ? "text-[#1A231E] hover:bg-[#EAF1ED]" : "text-white hover:bg-white/10"
                }`}
                data-testid="navbar-signin-btn"
              >
                <LogIn className="w-4 h-4 mr-1" />
                Sign in
              </button>
              <button
                onClick={goToSignIn}
                className="hidden sm:inline-flex btn-primary !py-2 !px-4 text-sm"
                data-testid="navbar-get-started-btn"
              >
                Get Started
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          )}
          <button
            className={`md:hidden p-2 rounded-lg ${isLight ? "hover:bg-[#EAF1ED]" : "hover:bg-white/10 text-white"}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="navbar-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="md:hidden border-t border-[#E4E1D9] bg-[#F7F6F2]">
          <div className="container-z py-3 flex flex-col gap-2">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[#1A231E] py-2"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                goToSignIn();
              }}
              className="btn-primary mt-1 justify-center"
              data-testid="mobile-get-started-btn"
            >
              Get Started <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
